import { Component, OnDestroy, OnInit, TemplateRef, inject, input, model } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { SubSink } from 'subsink';
import { TimeBlockAbsenceModel } from '../../../../../core/models/timeblock/time-block.model';
import { AbsenceReasonsHttpService } from '../../../../../core/state/settings/absence-reasons/absence-reasons-http.service';
import { DropdownOption, TimeOfDayValues } from '../../../../../shared/data-types/dropdown-option';
import {
  entityToDropdownOption,
  observableToDropdownOptions,
} from '../../../../../shared/functions/dropdown-functions';
import { CustomFormValidators } from '../../../../../shared/validators/custom-form.validators';
import { IntervalTypes } from '../../../calendar-types';
import { ITimeBlockComponentItem } from '../../../components/time-block/types/time-block-component-item-types';
import { TimeBlockType } from '../../../components/time-block/types/time-block-types';
import { CalendarService } from '../../../services/calendar.service';
import { AbsencePopupComponent } from './absence-popup/absence-popup.component';

@Component({
  selector: 'app-absence-type-dialog',
  templateUrl: './absence-type-dialog.component.html',
  standalone: false,
})
export class AbsenceTypeDialogComponent implements OnInit, OnDestroy {
  // Inputs
  readonly workTimeStart = input.required<Date>();
  readonly workTimeEnd = input.required<Date>();
  readonly selectedTimeBlock = input.required<ITimeBlockComponentItem>();
  readonly footer = input.required<TemplateRef<any>>();
  readonly timeOfDaySelectable = input.required<boolean>(); // Show / hide radio buttons.
  readonly fixIntervalDropdownVisible = model(false);
  readonly absenceTypeForm = model.required<FormGroup>();

  // DI
  public calendarService = inject(CalendarService);
  public absenceReasonOptions: DropdownOption[];

  // Constants
  public readonly popupContent = AbsencePopupComponent;
  public readonly faCircleInfo = faCircleInfo;
  private readonly subs = new SubSink();
  private readonly customFormValidators = inject(CustomFormValidators);
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly absenceReasonsService = inject(AbsenceReasonsHttpService);

  ngOnInit(): void {
    this.initForm();

    this.fixIntervalDropdownVisible.set(
      this.selectedTimeBlock()?.timeBlockModel.fixInterval !== null,
    );

    this.subs.sink = observableToDropdownOptions(
      this.absenceReasonsService.getAbsenceReasons(),
    ).subscribe((options) => {
      this.absenceReasonOptions = options;
      let selectedAbsenceReasonOption = options[0];
      const timeBlockModel = this.selectedTimeBlock().timeBlockModel;
      if (
        timeBlockModel instanceof TimeBlockAbsenceModel &&
        timeBlockModel.type === TimeBlockType.ExistingBlock
      ) {
        selectedAbsenceReasonOption = entityToDropdownOption(timeBlockModel.reason);
      }

      this.absenceTypeForm().patchValue({
        reason: selectedAbsenceReasonOption,
      });

      this.setTimeIntervalDropdown(+selectedAbsenceReasonOption.value);
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public absenceReasonChanged(absenceReasonOption: DropdownOption): void {
    this.setTimeIntervalDropdown(+absenceReasonOption.value);
  }

  private initForm(): void {
    let timeOfDay = TimeOfDayValues[0];
    // fixInterval can be either half-day or full-day.
    if (this.selectedTimeBlock().timeBlockModel.fixInterval) {
      timeOfDay = TimeOfDayValues.find(
        (val) => val.value === this.selectedTimeBlock().timeBlockModel.fixInterval,
      );
    }

    this.absenceTypeForm.set(
      this.formBuilder.group(
        {
          reason: [null, Validators.required],
          dateFrom: [this.selectedTimeBlock().timeBlockModel.start, Validators.required],
          dateTo: [this.selectedTimeBlock().timeBlockModel.end, Validators.required],
          description: [this.selectedTimeBlock().timeBlockModel.description],
          timeFrom: [this.selectedTimeBlock().timeBlockModel.start, Validators.required],
          timeTo: [this.selectedTimeBlock().timeBlockModel.end, Validators.required],
          timeOfDay: [timeOfDay],
        },
        {
          validators: this.customFormValidators.endAfterStart.bind(this.customFormValidators),
        },
      ),
    );

    if (this.timeOfDaySelectable()) {
      const intervalType =
        this.selectedTimeBlock().timeBlockModel.fixInterval === null
          ? IntervalTypes.VariableInterval
          : IntervalTypes.FixInterval;
      this.absenceTypeForm().addControl(
        'interval',
        new FormControl(intervalType, Validators.required),
      );
      return;
    }
  }

  private setTimeIntervalDropdown(id: number): void {
    if (id === 1 || id === 2 || id === 4) {
      // Todo: Load ids from backend or from config file.
      this.fixIntervalDropdownVisible.set(true);
      return;
    }

    this.fixIntervalDropdownVisible.set(false);
  }
}
